import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ===== Prem A/B — spot-managed real book vs premium-managed shadow book =====
   Every fib-vwap entry is mirrored into a twin managed purely in premium terms
   (server lib/shadow_prem.py). Entries are identical by construction, so the
   P&L delta per pair isolates the management basis. Paper only. */

const rup = (v) => v == null ? "—" : `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const fmtT = (s) => s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";

export default function ShadowPrem() {
  const [shadow, setShadow] = useState(null);
  const [real, setReal] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, m] = await Promise.all([
          axios.get("/api/shadow-prem/status", { timeout: 15000 }),
          axios.get("/api/monitor/status", { timeout: 15000 }),
        ]);
        setShadow(s.data);
        setReal((m.data?.trades || []).filter(t => String(t.strategy || "").startsWith("fib-vwap")));
      } catch {}
    };
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const pairs = useMemo(() => {
    if (!shadow) return [];
    const realById = new Map(real.map(t => [String(t.id), t]));
    return (shadow.twins || []).map(tw => ({ tw, rt: realById.get(String(tw.realId)) }))
      .sort((a, b) => String(b.tw.time).localeCompare(String(a.tw.time)));
  }, [shadow, real]);

  const totals = useMemo(() => {
    // matched + both closed = the honest comparison set
    const done = pairs.filter(p => p.rt && p.tw.status === "CLOSED" && p.rt.status === "CLOSED"
      && p.tw.netPnl != null && p.rt.netPnl != null);
    return {
      n: done.length,
      spot: done.reduce((a, p) => a + p.rt.netPnl, 0),
      prem: done.reduce((a, p) => a + p.tw.netPnl, 0),
      premWins: done.filter(p => p.tw.netPnl > p.rt.netPnl).length,
    };
  }, [pairs]);

  if (!shadow) return <div className="loading"><div className="spinner" /><p>Loading shadow book…</p></div>;
  const delta = totals.prem - totals.spot;

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h2>Prem A/B</h2>
          <p className="dashboard-subtitle">
            Same entries, two managers: the real book (SPOT-based SL/trailing) vs a shadow twin book (PREMIUM-based SL/target/tiers/trailing). Shift to whichever proves better. Paper only — twins never trade.
          </p>
        </div>
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12.5 }}>
        <span>Matched closed pairs: <b>{totals.n}</b></span>
        <span>Spot book: <b className={totals.spot >= 0 ? "positive" : "negative"}>{rup(totals.spot)}</b></span>
        <span>Premium book: <b className={totals.prem >= 0 ? "positive" : "negative"}>{rup(totals.prem)}</b></span>
        <span>Δ (premium − spot): <b className={delta >= 0 ? "positive" : "negative"} style={{ fontSize: 14 }}>{rup(delta)}</b></span>
        <span>Premium better on <b>{totals.premWins}/{totals.n}</b> pairs</span>
        <span style={{ marginLeft: "auto", fontSize: 10.5, color: "var(--muted2)" }}>{shadow.openCount} twins open · Δ-est {shadow.params?.deltaEst}</span>
      </div>

      <div className="tl-table-wrap">
        <table className="stocks-table" style={{ width: "100%" }}>
          <thead><tr>
            <th>Time</th><th>Book</th><th>Contract</th><th>Entry ₹</th>
            <th>Spot exit</th><th>Spot net</th>
            <th>Prem exit</th><th>Prem net</th>
            <th>Δ pair</th>
          </tr></thead>
          <tbody>
            {pairs.map(({ tw, rt }) => {
              const d = (tw.netPnl != null && rt?.netPnl != null) ? tw.netPnl - rt.netPnl : null;
              return (
                <tr key={tw.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtT(tw.time)}</td>
                  <td style={{ fontWeight: 600 }}>{tw.strategy === "fib-vwap-guarded" ? `${tw.index} Gd` : tw.index}</td>
                  <td style={{ fontSize: 10.5 }}>{tw.tradingsymbol}</td>
                  <td>₹{tw.entryPremium?.toFixed(1)}</td>
                  <td style={{ fontSize: 11 }}>{rt ? (rt.status === "OPEN" ? "OPEN" : `${rt.exitReason || ""} @ ₹${rt.exitPremium ?? "—"}`) : "—"}</td>
                  <td className={rt?.netPnl >= 0 ? "positive" : "negative"}>{rt ? (rt.status === "OPEN" ? "…" : rup(rt.netPnl)) : "—"}</td>
                  <td style={{ fontSize: 11 }}>{tw.status === "OPEN" ? `OPEN (floor ₹${tw.floor ?? tw.slPrem})` : `${tw.exitReason} @ ₹${tw.exitPremium}`}</td>
                  <td className={tw.netPnl >= 0 ? "positive" : "negative"}>{tw.status === "OPEN" ? "…" : rup(tw.netPnl)}</td>
                  <td className={d == null ? "" : d >= 0 ? "positive" : "negative"} style={{ fontWeight: 700 }}>{d == null ? "…" : rup(d)}</td>
                </tr>
              );
            })}
            {!pairs.length && <tr><td colSpan={9} style={{ color: "var(--muted2)" }}>No twins yet — the shadow book mirrors every new fib-vwap trade from deployment onward.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 10, color: "var(--faint2)", marginTop: 8, lineHeight: 1.5 }}>
        Twin rules (premium space): SL = entry − spotRisk×{shadow.params?.deltaEst} · target = entry + spotTargetDist×{shadow.params?.deltaEst} · tiers +{(shadow.params?.tier1Pct * 100) || 15}%/+{(shadow.params?.tier2Pct * 100) || 40}% book {(shadow.params?.releasePct * 100) || 40}% · breakeven floor at entry premium once armed · trail {shadow.params?.trailMult}×premRisk behind peak · EOD 15:28. Comparison counts only pairs where BOTH sides are closed.
      </div>
    </div>
  );
}
